import React, {
  createContext,
  useContext,
  useReducer,
  type ReactNode,
} from "react";

// --- Types ---
type NotificationSettings = {
  chatNotification: boolean;
  callNotification: boolean;
};

type Action =
  | { type: "TOGGLE_CHAT_NOTIFICATION"; payload: boolean }
  | { type: "TOGGLE_CALL_NOTIFICATION"; payload: boolean }
  | { type: "RESET_TO_DEFAULTS" };

type NotificationSettingsContextType = {
  state: NotificationSettings;
  dispatch: React.Dispatch<Action>;
};

import { safeStorage } from "../utils/safeStorage";

// --- Constants ---
const LOCAL_STORAGE_KEY = "notification-settings";

const DEFAULT_SETTINGS: NotificationSettings = {
  chatNotification: true,
  callNotification: true,
};

// --- Reducer ---
const persist = (state: NotificationSettings) => {
  try {
    safeStorage.setLocalItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn("Failed to persist notification settings:", err);
  }
};

const reducer = (
  state: NotificationSettings,
  action: Action,
): NotificationSettings => {
  let newState: NotificationSettings;

  switch (action.type) {
    case "TOGGLE_CHAT_NOTIFICATION":
      newState = { ...state, chatNotification: action.payload };
      break;
    case "TOGGLE_CALL_NOTIFICATION":
      newState = { ...state, callNotification: action.payload };
      break;
    case "RESET_TO_DEFAULTS":
      newState = DEFAULT_SETTINGS;
      break;
    default:
      newState = state;
  }

  persist(newState);
  return newState;
};

// --- Context ---
const NotificationSettingsContext = createContext<
  NotificationSettingsContextType | undefined
>(undefined);

// --- Provider ---
export const NotificationSettingsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, DEFAULT_SETTINGS, () => {
    try {
      const stored = safeStorage.getLocalItem(LOCAL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  return (
    <NotificationSettingsContext.Provider value={{ state, dispatch }}>
      {children}
    </NotificationSettingsContext.Provider>
  );
};

// --- Hook ---
export const useNotificationSettings = () => {
  const context = useContext(NotificationSettingsContext);
  if (!context) {
    throw new Error(
      "useNotificationSettings must be used within a NotificationSettingsProvider",
    );
  }
  return context;
};
